import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Download,
  FileText,
  FileSignature,
  Archive,
} from "lucide-react";

export default function ClientCediblePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingAll, setDownloadingAll] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cedible");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const download = async (r) => {
    try {
      const res = await api.get(`/cedible/${r.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.filename || `Cedible_${r.folio || r.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Error al descargar");
    }
  };

  const downloadAll = async () => {
    if (items.length === 0) return;
    setDownloadingAll(true);
    try {
      // Sequential download of each PDF (browser will handle the flurry)
      for (const r of items) {
        await download(r);
        await new Promise((res) => setTimeout(res, 300));
      }
      toast.success(`${items.length} cedible(s) descargado(s)`);
    } finally {
      setDownloadingAll(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-6 md:p-10">
        <PageHeader
          eyebrow="Documentos"
          title="Mis Cedibles"
          description="Guías de despacho (PDF) enviadas por EXIS Pharma como copia CEDIBLE. Descarga los archivos originales firmados."
          actions={
            items.length > 0 ? (
              <button
                onClick={downloadAll}
                disabled={downloadingAll}
                data-testid="client-cedible-download-all"
                className="flex items-center gap-2 px-5 py-3 bg-[#D95A40] hover:bg-[#c94d33] disabled:opacity-50 text-white font-bold uppercase tracking-[0.15em] text-xs transition-colors"
              >
                <Archive className="w-4 h-4" />
                {downloadingAll ? "Descargando..." : "Descargar todos"}
              </button>
            ) : null
          }
        />

        {loading && (
          <div className="bg-white border border-slate-200 p-10 text-center text-slate-500">
            Cargando...
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="bg-white border border-slate-200 p-12 text-center">
            <FileSignature className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <div className="text-slate-500">
              Aún no has recibido cedibles PDF.
            </div>
            <div className="text-xs text-slate-400 mt-2">
              EXIS Pharma te enviará aquí las guías de despacho en PDF cuando
              estén disponibles.
            </div>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="grid gap-3">
            {items.map((r) => (
              <div
                key={r.id}
                data-testid={`client-cedible-row-${r.id}`}
                className="bg-white border border-slate-200 p-5 hover:border-[#0B1B3D] hover:shadow-sm transition-all flex items-center justify-between gap-4 flex-wrap"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-11 h-11 bg-red-100 text-red-700 flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5" strokeWidth={2.2} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-[#0B1B3D] text-lg truncate">
                      {r.filename}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                      {r.folio && (
                        <span>
                          Folio GD:{" "}
                          <span className="text-[#D95A40] font-bold">
                            {r.folio}
                          </span>
                        </span>
                      )}
                      <span>
                        {((r.size_bytes || 0) / 1024).toFixed(1)} KB
                      </span>
                      <span>
                        Recibido {new Date(r.uploaded_at).toLocaleString("es-CL")}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => download(r)}
                  data-testid={`client-cedible-download-${r.id}`}
                  className="flex items-center gap-2 px-4 py-2.5 bg-[#D95A40] hover:bg-[#c94d33] text-white text-xs font-bold uppercase tracking-wider transition-colors shrink-0"
                >
                  <Download className="w-4 h-4" /> Descargar
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
